import { Button, Descriptions, Grid, Tabs } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as permissionKind from '../../../../../shared/permissions';
// eslint-disable-next-line max-len
import { type AudiSalesForceModuleWithPermissionsSpecsFragment } from '../../../../api/fragments/AudiSalesForceModuleWithPermissionsSpecs';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../../utilities/useFormattedSimpleVersioning';
import AudiSalesForceModuleActions from './AudiSalesForceModuleActions';
import AudiSalesForceModuleInnerPage from './AudiSalesForceModuleInnerPage';

export type AudiSalesForceModulePageProps = {
    module: AudiSalesForceModuleWithPermissionsSpecsFragment;
};

export enum HeaderTabs {
    MainDetails = 'mainDetails',
    Authorization = 'authorization',
}

const AudiSalesForceModulePage = ({ module }: AudiSalesForceModulePageProps) => {
    const { t } = useTranslation(['audiSalesForceModuleDetails']);
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(HeaderTabs.MainDetails);

    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: module.versioning,
        timeZone: module.company.timeZone,
    });

    const screens = Grid.useBreakpoint();
    const permissions = {
        hasDeletePermission: hasPermissions(module.permissions, [permissionKind.deleteModule]),
    };

    const content = (
        <Descriptions column={{ xs: 1, md: 2 }} bordered>
            <Descriptions.Item label={t('audiSalesForceModuleDetails:content.displayName')}>
                {module.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('audiSalesForceModuleDetails:content.company')}>
                {module.company.displayName}
            </Descriptions.Item>
            <Descriptions.Item {...t('common:fields.updated', { returnObjects: true, offset })}>
                {updated}
            </Descriptions.Item>
        </Descriptions>
    );

    return (
        <ConsolePageWithHeader
            content={content}
            extra={<AudiSalesForceModuleActions module={module} permissions={permissions} />}
            footer={[
                <Button form="updateAudiSalesForceModuleForm" htmlType="submit" type="primary">
                    {t('audiSalesForceModuleDetails:actions.update')}
                </Button>,
            ]}
            header={{
                footer: (
                    <Tabs activeKey={activeTab} onChange={(activeKey: HeaderTabs) => setActiveTab(activeKey)}>
                        <Tabs.TabPane
                            key={HeaderTabs.MainDetails}
                            tab={t('audiSalesForceModuleDetails:tabs.mainDetails.title')}
                        />
                        <Tabs.TabPane
                            key={HeaderTabs.Authorization}
                            tab={t('audiSalesForceModuleDetails:tabs.authorization.title')}
                        />
                    </Tabs>
                ),
            }}
            onBack={() => navigate('/admin/system/modules')}
            title={
                screens.md ? t('audiSalesForceModuleDetails:title', { name: module.displayName }) : module.displayName
            }
        >
            <AudiSalesForceModuleInnerPage activeTab={activeTab} module={module} />
        </ConsolePageWithHeader>
    );
};

export default AudiSalesForceModulePage;
